import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Bell, ExternalLink, X } from 'lucide-react';
import { createPortal } from 'react-dom';
import api from '../../services/api';
import { useI18n } from '../../context/useI18n';

/**
 * Campanilla shell: notificaciones in_app (NotificationDatabase vía /api/notifications).
 * Orden canónico en API: pago pendiente → adherencia → asistencia → resto.
 * Admin: acción «Ir» navega al panel relacionado (vigencias / activar usuario).
 * Usuario: «Ir» abre link de pago (meta.action_url) o navega a Mi Cuenta / vista (meta.action_view).
 * Panel siempre `fixed` y clamp al viewport (evita corte fuera de pantalla en coach WL / nav wrap).
 */
function resolveAdminAction(n) {
  const meta = n?.meta || {};
  if (meta.action_view) {
    return {
      view: String(meta.action_view),
      userId: meta.action_user_id ?? meta.payer_user_id ?? null,
      openActivate: Boolean(meta.open_activate),
    };
  }
  const tipo = String(n?.tipo || '').toLowerCase();
  const evento = String(meta.evento || '').toLowerCase();
  if (tipo === 'activacion_pendiente' || evento === 'activation.pending') {
    return {
      view: 'adminusers',
      userId: meta.action_user_id ?? meta.payer_user_id ?? null,
      openActivate: true,
    };
  }
  if (
    tipo === 'pago_pendiente'
    || evento === 'payment.pending'
    || evento === 'payment.due'
  ) {
    return {
      view: 'modulevigencias',
      userId: meta.payer_user_id ?? meta.action_user_id ?? null,
      openActivate: false,
    };
  }
  return null;
}

/** CTA para cualquier usuario (pago ciclo, renovación, etc.). */
function resolveUserCta(n) {
  const meta = n?.meta || {};
  const url = meta.action_url ? String(meta.action_url).trim() : '';
  if (url) return { type: 'url', url };
  if (meta.action_view) {
    return {
      type: 'view',
      view: String(meta.action_view),
      userId: meta.action_user_id ?? meta.payer_user_id ?? null,
      openActivate: Boolean(meta.open_activate),
    };
  }
  const tipo = String(n?.tipo || '').toLowerCase();
  if (tipo === 'ciclo_renovacion' || tipo === 'pago_pendiente') {
    return { type: 'view', view: 'myaccount', userId: null, openActivate: false };
  }
  return null;
}

function computePanelBox(anchorEl) {
  if (!anchorEl || typeof window === 'undefined') {
    return {
      top: 64,
      left: 8,
      width: Math.min(320, 280),
      maxHeight: 320,
    };
  }
  const rect = anchorEl.getBoundingClientRect();
  const pad = 8;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const isMobile = vw < 640;
  const width = isMobile
    ? Math.min(vw - pad * 2, 360)
    : Math.min(320, vw - pad * 2);
  let left = isMobile
    ? (vw - width) / 2
    : rect.right - width;
  if (left < pad) left = pad;
  if (left + width > vw - pad) left = Math.max(pad, vw - pad - width);

  const preferredMaxH = isMobile ? Math.min(vh * 0.7, 420) : Math.min(vh * 0.6, 360);
  let top = rect.bottom + pad;
  if (top + preferredMaxH > vh - pad) {
    const above = rect.top - preferredMaxH - pad;
    top = above >= pad ? above : pad;
  }
  const maxHeight = Math.max(180, Math.min(preferredMaxH, vh - top - pad));

  return { top, left, width, maxHeight };
}

export default function ShellNotificationBell({ isAdmin = false, onAdminNavigate = null }) {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [panelBox, setPanelBox] = useState(() => computePanelBox(null));
  const wrapRef = useRef(null);
  const panelRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/notifications');
      setItems(Array.isArray(r.data?.data) ? r.data.data : []);
    } catch {
      setItems([]);
    }
  }, []);

  const reposition = useCallback(() => {
    setPanelBox(computePanelBox(wrapRef.current));
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, 60_000);
    const onRefresh = () => load();
    window.addEventListener('d28d:notifications:refresh', onRefresh);
    return () => {
      clearInterval(timer);
      window.removeEventListener('d28d:notifications:refresh', onRefresh);
    };
  }, [load]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    reposition();
    const onWin = () => reposition();
    window.addEventListener('resize', onWin);
    window.addEventListener('scroll', onWin, true);
    return () => {
      window.removeEventListener('resize', onWin);
      window.removeEventListener('scroll', onWin, true);
    };
  }, [open, reposition]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      const inBell = wrapRef.current?.contains(e.target);
      const inPanel = panelRef.current?.contains(e.target);
      if (!inBell && !inPanel) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const unread = items.filter((n) => !n.leida);
  const unreadCount = unread.length;

  const markOne = async (id) => {
    try {
      await api.patch(`/notifications/${id}/read`);
      setItems((prev) => prev.map((n) => (Number(n.id) === Number(id) ? { ...n, leida: true } : n)));
    } catch { /* noop */ }
  };

  const markAll = async () => {
    const pending = unread.slice(0, 30);
    await Promise.all(pending.map((n) => api.patch(`/notifications/${n.id}/read`).catch(() => null)));
    await load();
  };

  const handleItemClick = async (n) => {
    await markOne(n.id);
    const meta = n?.meta || {};
    const paymentUrl = meta.action_url ? String(meta.action_url).trim() : '';
    // Link de pago: prioriza checkout para cualquier rol (renovación de ciclo).
    if (paymentUrl) {
      setOpen(false);
      window.open(paymentUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    if (isAdmin && typeof onAdminNavigate === 'function') {
      const action = resolveAdminAction(n);
      if (action) {
        setOpen(false);
        onAdminNavigate(action);
        return;
      }
    }
    const userCta = resolveUserCta(n);
    if (userCta?.type === 'view' && typeof onAdminNavigate === 'function') {
      setOpen(false);
      onAdminNavigate({
        view: userCta.view,
        userId: userCta.userId,
        openActivate: userCta.openActivate,
      });
    }
  };

  const panel = open ? (
    <>
      <button
        type="button"
        className="fixed inset-0 z-[209] bg-black/40 sm:bg-black/20"
        aria-label={t('common.close', 'Cerrar')}
        onClick={() => setOpen(false)}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-label={t('nav.notifications', 'Notificaciones')}
        className="fixed z-[210] rounded-xl border border-stone-200 bg-white shadow-xl text-stone-900 ui-light-panel surface-readable flex flex-col overflow-hidden"
        style={{
          top: panelBox.top,
          left: panelBox.left,
          width: panelBox.width,
          maxHeight: panelBox.maxHeight,
        }}
      >
        <div className="flex items-center justify-between gap-2 px-3 py-2.5 border-b border-stone-100 shrink-0">
          <p className="text-sm font-semibold m-0">{t('nav.notifications', 'Notificaciones')}</p>
          <div className="flex items-center gap-2">
            {unreadCount > 0 ? (
              <button type="button" className="text-xs text-sky-700 font-medium min-h-8 px-1" onClick={markAll}>
                {t('nav.mark_all_read', 'Marcar leídas')}
              </button>
            ) : null}
            <button
              type="button"
              className="p-1.5 rounded-lg text-stone-500 hover:bg-stone-100 sm:hidden"
              aria-label={t('common.close', 'Cerrar')}
              onClick={() => setOpen(false)}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        <ul className="flex-1 min-h-0 overflow-y-auto divide-y divide-stone-100 overscroll-contain">
          {items.length === 0 ? (
            <li className="px-3 py-4 text-sm text-stone-500">
              {t('nav.no_notifications', 'Sin notificaciones')}
            </li>
          ) : (
            items.slice(0, 20).map((n) => {
              const showGo = Boolean(resolveUserCta(n) || (isAdmin ? resolveAdminAction(n) : null));
              return (
                <li key={n.id}>
                  <button
                    type="button"
                    className={`w-full text-left px-3 py-3 text-sm hover:bg-stone-50 ${n.leida ? 'text-stone-500' : 'text-stone-900 font-medium'}`}
                    onClick={() => handleItemClick(n)}
                  >
                    <p className="leading-snug break-words m-0">{n.mensaje || n.tipo}</p>
                    <p className="text-[11px] text-stone-400 mt-1 flex items-center justify-between gap-2 m-0">
                      <span>{n.creado_en ? String(n.creado_en).slice(0, 16).replace('T', ' ') : ''}</span>
                      {showGo ? (
                        <span className="inline-flex items-center gap-1 text-sky-700 font-semibold shrink-0">
                          <ExternalLink className="w-3 h-3" aria-hidden />
                          {t('nav.notif_go', 'Ir')}
                        </span>
                      ) : null}
                    </p>
                  </button>
                </li>
              );
            })
          )}
        </ul>
      </div>
    </>
  ) : null;

  return (
    <div className="relative shrink-0" ref={wrapRef}>
      <button
        type="button"
        className="relative p-2.5 min-h-10 min-w-10 rounded-lg text-[var(--d28d-text)] hover:bg-white/10"
        aria-label={t('nav.notifications', 'Notificaciones')}
        aria-expanded={open}
        onClick={() => {
          setOpen((v) => !v);
          if (!open) load();
        }}
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 ? (
          <span className="absolute -top-0.5 -right-0.5 min-w-[1.1rem] h-[1.1rem] px-0.5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        ) : null}
      </button>
      {typeof document !== 'undefined' && panel ? createPortal(panel, document.body) : null}
    </div>
  );
}
